import { useCallback, useState } from 'react';

import useOrganization from '~/components/OrganizationContext';
import type {
  ClaimFlagConfigurationCreateJson,
  ClaimFlagConfigurationUpdateJson,
} from '~/server_shared/openapi/data-contracts';
import { reportAxiosError } from '~/Utils';

import type { ClaimFlagConfiguration, ClaimFlagConfigurationsResponse } from '../../../../ClaimFlags/types';
import {
  createClaimFlagConfiguration,
  reorderClaimFlagConfigurations,
  updateClaimFlagConfiguration,
} from '../apiService';

interface PartialUseOrganizationContext {
  organizationId: number;
  claimFlagConfigurations: ClaimFlagConfiguration[];
  isLoadingClaimFlagConfigurations: boolean;
  isErrorClaimFlagConfigurations: boolean;
  reloadClaimFlagConfigurations: () => Promise<ClaimFlagConfigurationsResponse | undefined>;
}

const useClaimFlagConfigurationAndMutations = () => {
  const [isUpdating, setIsUpdating] = useState(false);
  const {
    organizationId,
    claimFlagConfigurations,
    isLoadingClaimFlagConfigurations: isLoading,
    isErrorClaimFlagConfigurations: isError,
    reloadClaimFlagConfigurations: reloadData,
  } = useOrganization() as PartialUseOrganizationContext;

  const runMutation = useCallback(
    async (mutation: () => Promise<unknown>) => {
      setIsUpdating(true);
      try {
        await mutation();
      } catch (error) {
        reportAxiosError(error);
        throw error;
      } finally {
        // Resync on both success and failure (a failed reorder's optimistic order is reverted here),
        // clearing the saving flag even if the resync itself throws.
        try {
          await reloadData();
        } finally {
          setIsUpdating(false);
        }
      }
    },
    [reloadData]
  );

  const create = useCallback(
    (values: ClaimFlagConfigurationCreateJson) =>
      runMutation(() => createClaimFlagConfiguration(organizationId, values)),
    [organizationId, runMutation]
  );

  const update = useCallback(
    (flagKey: string, values: ClaimFlagConfigurationUpdateJson) =>
      runMutation(() => updateClaimFlagConfiguration(organizationId, flagKey, values)),
    [organizationId, runMutation]
  );

  const reorder = useCallback(
    (flagKeys: string[]) => runMutation(() => reorderClaimFlagConfigurations(organizationId, flagKeys)),
    [organizationId, runMutation]
  );

  return {
    // Pass the context value straight through — it's already a stable reference (a stable empty-array
    // const when unloaded). A `|| []` fallback here would mint a fresh array each render and break the
    // render-phase state sync in ClaimFlagConfiguration.tsx (infinite setState loop).
    claimFlagConfigurations,
    isUpdating,
    isLoading,
    isError,
    reloadData,
    create,
    update,
    reorder,
  };
};

export default useClaimFlagConfigurationAndMutations;
